import { ID, StoreActions } from '@datorama/akita';
import { Content, Schema, Field, SquidexDate } from '../../squidex';
import { BaseStore } from './base.store';
import { BaseQuery } from './base.query';
import { BaseService } from './base.service';
import { of, Observable, from } from 'rxjs';
import { tap, map, mergeMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { FilterPagination } from './filter.pagination';
import { DynamicSchemaFacade } from '../dynamic-schema/dynamic-schema.facade';
import { ESchemaContentStatus } from './enum';
import { DatePipe } from '@angular/common';
import { AppToken } from '../../../models/app-token';

const EMPTY = '';

export abstract class BaseFacade {

  readonly datePipe: DatePipe;

  protected constructor(
    private service: BaseService,
    private store: BaseStore,
    private query: BaseQuery,
    readonly dynamicSchemaFacade: DynamicSchemaFacade,
    private readonly translateService: TranslateService,
    readonly facadeName: string
  ) {

    this.datePipe = new DatePipe(this.getCurrentLanguage());
  }

  getService(): BaseService {
    return this.service;
  }

  getStore(): BaseStore {
    return this.store;
  }

  getQuery(): BaseQuery {
    return this.query;
  }

  initializeSchema(): Observable<Schema> {
    return this.getSchema().pipe(
      tap(schema => this.getStore().update({ schema: schema }))
    );
  }

  add(contentData: any): Promise<Content> {
    contentData.deleted = { iv: false };

    return this.getService().addContent(contentData)
      .toPromise().then(contentBack => {
        this.getStore().add(contentBack);
        return contentBack;
      });
  }

  addObs(contentData: any): Observable<Content> {
    contentData.deleted = { iv: false };

    return this.getService().addContent(contentData)
      .pipe(map(contentBack => {
        this.getStore().add(contentBack);
        return contentBack;
      }));
  }

  create(contentData: any): Observable<Content> {
    contentData.deleted = { iv: false };

    return this.getService().addContent(contentData)
      .pipe(
        tap(v => {
          this.getStore().add(v);
        })
      );
  }

  createAsPublished(contentData: any): Observable<Content> {
    contentData.deleted = { iv: false };

    return this.getService().createAndPublishContent(contentData)
      .pipe(
        tap(v => {
          this.getStore().add(v);
        })
      );
  }

  createDraft(contentId: string): Observable<Content> {
    return this.getService().createDraft(contentId)
      .pipe(
        tap(v => {
          this.getStore().add(v);
        })
      );
  }

  getSchema(): Observable<Schema> {
    return this.dynamicSchemaFacade.getSchemaDefinition() as Observable<Schema>;
  }

  get(id: ID, draftMode: boolean = false): Observable<Content> {
    return this.getService().getContentById(id, draftMode).pipe(mergeMap((content) => {
      this.getStore().add(content);
      return of(content);
    })) as Observable<Content>;
  }

  getAll(): Observable<Content[]> {
    return this.getService().countContent().pipe(mergeMap (totalCount => {
      if (this.getStore().getValue().totalCount === totalCount) {
        return of(Array.from(Object.values(this.getQuery().getValue().entities)));
      }
      return this.getService().getAll().pipe(mergeMap(data => {
        this.getStore().add(data.items);
        this.getStore().update({ totalCount: data.total });
        return of(data.items);
      })) as Observable<Content[]>})
    );

  }

  doPagination(filterPagination: FilterPagination): Observable<Content[]> {
    if (filterPagination.from < 0) { throw (new Error('try to paginate at index < 0')); }

    if (this.getStore().hasPaginateContents(filterPagination)) {
      return this.getQuery().getPaginateData(filterPagination);
    }

    const contents = this.getService().doPagination(filterPagination).pipe(mergeMap((contentList) => {
      this.getStore().update({ totalCount: contentList.total });

      this.getStore().updatePaginate(contentList.items, filterPagination);
      return of(contentList.items);
    }));

    return contents as Observable<Content[]>;
  }

  update(contentId: string, contentData: any): Observable<Content> {
    return this.getService().updateContent(contentId, contentData).
      pipe(
        tap(contentBack => {
          this.getStore().update(contentId, u => {
            return contentBack;
          });
        })
      );
  }

  removeStore (id: string): any {
    this.getStore().remove(id);
  }

  remove(id: ID, draftMode: boolean = false): Observable<Content> {
    return this.getService().removeContent(id, draftMode).pipe(
      tap(v => {
        this.getStore().remove(id);
      })
    );
  }

  removeDraft(contentId: string): Observable<Content> {
    return this.getService().deleteDraft(contentId).pipe(
      tap(v => {
        this.getStore().remove(contentId);
      })
    );
  }

  removeHard(id: ID): Observable<Content> {
    return this.getService().removeHardContent(id).pipe(
      tap(v => {
        this.getStore().remove(id);
      })
    );
  }


  fullCount(): Observable<number> {
    if (this.getQuery().getValue().totalCount && this.getQuery().getValue().totalCount > 0) {
      return of(this.getQuery().getValue().totalCount);
    }

    return this.getService().getTopX(1, undefined, '', ESchemaContentStatus.All)
      .pipe(map(object => {
        this.getStore().update({ totalCount: object.total });
        return object.total;
      }));
  }

  partialCount(): number {
    return this.getQuery().getCount();
  }

  filterCount(advancedFilter: any , eSchemaContentStatus: ESchemaContentStatus = ESchemaContentStatus.All): Observable<number> {
    const filterParms: FilterPagination = new FilterPagination()
    filterParms.from = 0;
    filterParms.to = 1;

    return this.getService().getFilteredPagination(filterParms, eSchemaContentStatus, advancedFilter )
      .pipe(map(content => content['total'])   );
  }

  getByCode(code: string, draftMode: boolean = false): Observable<Content> {
    const content = this.getQuery().selectAll({
      filterBy: entity => entity.data && entity.data['code']['iv'] === code
    }).pipe(
      mergeMap(v => {
        const value = v[0];
        if (!value) {
          return this.getService().getContentByCode(code, draftMode).pipe(map(val => {
            if (val) {
              this.getStore().add(val);
            }
            return val;
          }));
        }
        return of(value);
      })
    );
    return content as Observable<Content>;
  }

  getDefaultLanguage(): string {
    return this.translateService.defaultLang;
  }

  getCurrentLanguage(): string {
    return this.translateService.currentLang;
  }

  getTopX(top: number = 24,
    filter: string = null,
    orderBy: string = 'lastModified desc',
    eSchemaContentStatus: ESchemaContentStatus = ESchemaContentStatus.Published
  ): Observable<Content[]> {
    return this.getService().getTopX(top, filter, orderBy, eSchemaContentStatus).pipe(mergeMap((contentList) => {
      this.getStore().add(contentList.items);
      this.getStore().update({ totalCount: contentList.total });
      return of(contentList.items);
    })) as Observable<Content[]>;
  }

  getFiltered(top: number = 24,
    orderBy: string = 'lastModified desc',
    eSchemaContentStatus: ESchemaContentStatus = ESchemaContentStatus.Published,
    filter?: string
  ): Observable<Content[]> {
    return this.getService().getFiltered(top, orderBy, eSchemaContentStatus, filter).pipe(mergeMap((contentList) => {
      this.getStore().add(contentList.items);
      this.getStore().update({ totalCount: contentList.total });
      return of(contentList.items);
    })) as Observable<Content[]>;
  }

  validateUrl(filterParms: FilterPagination,
    eSchemaContentStatus: ESchemaContentStatus = ESchemaContentStatus.All,
    filterObject?: any, content: Content = null
  ): Observable<Number> {
    return this.getService().validateUrl(filterParms, eSchemaContentStatus, filterObject, content);
  }

  getFilteredPagination(
    filterParms: FilterPagination,
    eSchemaContentStatus: ESchemaContentStatus = ESchemaContentStatus.Published,
    filterObject?: any
  ): Observable<Content[]> {
    return this.getService().getFilteredPagination(filterParms, eSchemaContentStatus, filterObject).pipe(mergeMap((contentList) => {
      this.getStore().add(contentList.items);
      this.getStore().update({ totalCount: contentList.total });
      return of(contentList.items);
    })) as Observable<Content[]>;
  }

  getByObject(
    filterObject: any,
    eSchemaContentStatus: ESchemaContentStatus = ESchemaContentStatus.Published,
  ): Observable<Content[]> {
    return this.getService().getByObject(filterObject, eSchemaContentStatus).pipe(mergeMap((contentList) => {
      this.getStore().add(contentList.items);
      this.getStore().update({ totalCount: contentList.total });
      return of(contentList.items);
    })) as Observable<Content[]>;
  }

  getUnpublished(orderBy: string = 'lastModified desc', top: number = -1): Observable<Content[]> {
    return this.getService().getUnpublished(orderBy, top).pipe(mergeMap((contentList) => {
      this.getStore().add(contentList.items);
      this.getStore().update({ totalCount: contentList.total });
      return of(contentList.items);
    })) as Observable<Content[]>;
  }

  getAllOrdered(orderBy: string = 'id'): Observable<Content[]> {
    if (this.getQuery().getCount() === this.getQuery().getValue().totalCount) {
      return of(Array.from(Object.values(this.getQuery().getValue().entities)));
    }

    return this.getService().getAllOrdered(orderBy).pipe(mergeMap(data => {
      this.getStore().add(data.items);
      this.getStore().update({ totalCount: data.total });
      return of(data.items);
    })) as Observable<Content[]>;
  }

  getByParentId(id: string, parentFieldName: string, orderBy: string = 'id', draftMode: boolean = false): Observable<Content[]> {
    return this.getService().getByParentId(id, parentFieldName, orderBy, draftMode).pipe(mergeMap((contentList) => {
      this.getStore().add(contentList.items);
      this.getStore().update({ totalCount: contentList.total });
      return of(contentList.items);
    })) as Observable<Content[]>;
  }

  getData(content: Content, field: Field): any {
    return this.pullData(content, field.name, field.partitioning === 'language');
  }

  // same as getData, with different signature, get and put are synonyms
  pullData(content: Content, key: string, isLocalized: boolean = false, checkDefaultLanguage: boolean = false): any {
    if (!content || !content.data || !key) { return EMPTY; }

    const d = content.data[key];

    let data: any;

    if (d) {
      if (isLocalized) {
        data = d[this.getCurrentLanguage()];
        if (!data && checkDefaultLanguage) {
          data = d[this.getDefaultLanguage()];
        }
      } else {
        data = d['iv'];
      }
    } else {
      data = EMPTY;
    }

    return data;
  }

  setContent(content: Content, field: Field, newValue: string): any {
    newValue = this.fixFieldValue(field.properties.editor, newValue);
    return this.setData(content.data, field, newValue);
  }

  setData(data: any, field: Field, newValue: string): any {
    newValue = this.fixFieldValue(field.properties.editor, newValue);
    return this.putData(data, field.name, newValue, field.partitioning === 'language');
  }

  setDataValue(data: any, field: string, newValue: string, isLocalized: boolean = false): any {
    // newValue = this.fixFieldValue(field.properties.editor, newValue);
    return this.putData(data, field, newValue, isLocalized);
  }

  setDataAnyType(data: any, field: string, newValue: any, isLocalized: boolean = false): any {
    return this.putData(data, field, newValue, isLocalized);
  }

  // same as setData, with different signature, set and pull are synonyms
  private putData(data: any, key: string, newValue: any, isLocalized: boolean = false, field?: Field): any {
    if (!data || !key) { return null; }

    if (!data[key]) {
      data[key] = {};
    }

    if (field && field.properties.fieldType === 'Assets') {
      if (!data[key]) {
        data[key] = { 'iv': [] };
      }
      if (data[key].iv.length === 0) {
        data[key].iv = [];
      }
      if (Array.isArray(newValue)) {
        let tmp = [];

        data[key].iv.forEach(val => {
          if (!tmp.includes(val)) {
            tmp.push(val);
          }
        });

        newValue.forEach(val => {
          if (!tmp.includes(val)) {
            tmp.push(val);
          }
        });

        data[key].iv = tmp;
      } else {
        data[key].iv.push(newValue);
      }
    } else {
      if (field && field.properties.fieldType === 'Boolean') {
        if (newValue === '') {
          newValue = false;
        }
      } else {
        if (isLocalized) {
          data[key][this.getCurrentLanguage()] = newValue;
        } else {
          data[key]['iv'] = newValue;
        }
      }
    }
    return data;
  }

  putNumber(data: any, key: string, newValue: number): any {
    if (!data || !key) {
      return null;
    }

    if (!data[key]) {
      data[key] = {};
    }

    data[key]['iv'] = newValue;

    return data;
  }

  private fixFieldValue(editor: string, value: any): any {
    if (editor === "DateTime") {
      return new SquidexDate(value).format();
    } else if (editor === "Dropdown" && !Array.isArray(value)) {
      return [value];
    }
    return value;
  }

  translate(key: string | Array<string>, interpolateParams?: Object): Observable<string | any> {
    return this.translateService.get(key, interpolateParams);
  }

  getLastModifiedDate(content: Content, dateFormat: string = AppToken.ISO_DATES_DATE_TIME): string {
    if (!content) {
      return null;
    } else {
      return this.getLocalDateTime(content.lastModified, dateFormat);
    }
  }

  getLocalDateTime(date: Date, dateFormat: string = AppToken.ISO_DATES_DATE_TIME): string {
    return this.datePipe.transform(new Date(date), dateFormat);
  }

  updateStatus(contentId: string, status: string, dueTime: string) {
    return this.getService().updateContentStatus(contentId, status, dueTime).
      pipe(
        tap(contentBack => {
          this.getStore().update(contentId, u => {
            return contentBack;
          });
        })
      );
  }

}
